import { computed, type Ref } from 'vue'
import type { AISummaryResponse } from '@/types/dashboard'

/**
 * Composable for calculating AI summary progress metrics
 *
 * @param data - Reactive reference to AI summary response data
 * @returns Progress metrics (callCount, includedCallCount, skippedCallCount, analysisProgress)
 *
 * @example
 * ```ts
 * const { data } = useNextUpdatedWidgetData<AISummaryResponse, typeof props, 'data'>(props, 'data')
 * const { callCount, includedCallCount, skippedCallCount, analysisProgress } = useAiSummaryProgress(data)
 * ```
 */
export function useAiSummaryProgress (data: Ref<AISummaryResponse | null>) {
    const callCount = computed(() => data?.value?.Metadata?.callCount || 0)
    const includedCallCount = computed(() => data?.value?.Metadata?.includedCallCount || 0)
    const skippedCallCount = computed(() => callCount.value - includedCallCount.value)

    const analysisProgress = computed(() => {
        if (callCount.value === 0) return 0
        return (includedCallCount.value / callCount.value) * 100
    })

    return {
        callCount,
        includedCallCount,
        skippedCallCount,
        analysisProgress
    }
}
